import React from 'react';
import {
  Alert,
  AlertActionLink,
  Bullseye,
  Button,
  Checkbox,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Spinner,
  Title,
} from '@patternfly/react-core';
import { ExternalLinkAltIcon, KeyIcon, SyncAltIcon } from '@patternfly/react-icons';
import type { PubkeySummary } from '../../../../types/models/Pubkey';
import { MAX_KEYS } from './startGpuServerSteps';

interface KeysStepProps {
  pubkeys: PubkeySummary[];
  loading: boolean;
  loadError: Error | null;
  onReload: () => void;
  selectedIds: number[];
  onToggleKey: (id: number, checked: boolean) => void;
  /** A start refusal that belongs to this step (`data.pubkey_ids`), shown verbatim. */
  error: string | null;
}

const ACCEPTED_TYPES = 'Accepted key types: ed25519, RSA, ECDSA and FIDO security keys (sk-…).';

/**
 * Step 4 — choose 1 to 5 of the organisation's SSH public keys, installed on the server. Keys are
 * not validated when they are saved, so a bad key is only refused by the start itself. Adding a key
 * opens the key form in a new tab, so the wizard's selections survive; "Reload keys" then lists it.
 */
const KeysStep: React.FunctionComponent<KeysStepProps> = ({
  pubkeys,
  loading,
  loadError,
  onReload,
  selectedIds,
  onToggleKey,
  error,
}) => {
  // Keys without an id can't be sent (`pubkey_ids` are integers).
  const keys = pubkeys.filter((key): key is PubkeySummary & { id: number } => key.id !== null);
  const selectedCount = selectedIds.filter((id) => keys.some((key) => key.id === id)).length;

  const renderBody = () => {
    if (loadError !== null) {
      return (
        <Alert
          variant="warning"
          isInline
          title="Your SSH keys could not be loaded"
          actionLinks={<AlertActionLink onClick={onReload}>Retry</AlertActionLink>}
        >
          {loadError.message}
        </Alert>
      );
    }
    if (keys.length === 0 && loading) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading your SSH keys" />
        </Bullseye>
      );
    }
    if (keys.length === 0) {
      return (
        <EmptyState titleText="You need an SSH public key" icon={KeyIcon} variant="sm" headingLevel="h3">
          <EmptyStateBody>
            You log in to the server with an SSH key. Add one to your organisation, then reload the list.{' '}
            {ACCEPTED_TYPES}
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button
                variant="primary"
                component="a"
                href="/datacenter/pubkeys/create"
                target="_blank"
                rel="noopener"
                icon={<ExternalLinkAltIcon />}
                iconPosition="end"
              >
                Add a public key
              </Button>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                onClick={onReload}
                isLoading={loading}
                isDisabled={loading}
              >
                Reload keys
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }
    return (
      // The wizard's own Next is a submit button, so this form must never submit natively.
      <Form onSubmit={(event) => event.preventDefault()}>
        <FormGroup role="group" fieldId="gpu-keys" label="SSH keys" isRequired isStack>
          {keys.map((key) => {
            const checked = selectedIds.includes(key.id);
            return (
              <Checkbox
                key={key.id}
                id={`gpu-key-${key.id}`}
                label={key.label}
                isChecked={checked}
                isDisabled={!checked && selectedCount >= MAX_KEYS}
                onChange={(_event, value) => onToggleKey(key.id, value)}
              />
            );
          })}
          <FormHelperText>
            <HelperText isLiveRegion>
              <HelperTextItem>
                {selectedCount} of at most {MAX_KEYS} selected. {ACCEPTED_TYPES}
              </HelperTextItem>
            </HelperText>
          </FormHelperText>
        </FormGroup>
        <div>
          <Button
            variant="link"
            isInline
            component="a"
            href="/datacenter/pubkeys/create"
            target="_blank"
            rel="noopener"
            icon={<ExternalLinkAltIcon />}
            iconPosition="end"
          >
            Add a public key
          </Button>{' '}
          ·{' '}
          <Button variant="link" isInline onClick={onReload} isDisabled={loading}>
            Reload keys
          </Button>
        </div>
      </Form>
    );
  };

  return (
    <>
      <Title headingLevel="h2" size="lg" className="pf-v6-u-mb-sm">
        Choose your SSH keys
      </Title>
      {error !== null && (
        <Alert variant="danger" isInline title="These SSH keys cannot be used" className="pf-v6-u-mb-md">
          {error}
        </Alert>
      )}
      {renderBody()}
    </>
  );
};

export default KeysStep;
